import { Component } from 'react';
import sharedStyles from '../single-page.module.scss';
import { FullscreenHost } from '../fullscreen-host.component';
import styles from './x509-decoder.component.module.scss';

type LibState =
    | { kind: 'idle' }
    | { kind: 'loading' }
    | { kind: 'ready'; forge: any }
    | { kind: 'error'; message: string };

interface CertInfo {
    subject: { name: string; value: string }[];
    issuer: { name: string; value: string }[];
    serialNumber: string;
    notBefore: string;
    notAfter: string;
    signatureAlgorithm: string;
    publicKeyType: string;
    sans: string[];
    fingerprintSha256: string;
}

interface State {
    pemInput: string;
    info: CertInfo | null;
    error: string | null;
    lib: LibState;
}

const SAMPLE_PEM = '-----BEGIN CERTIFICATE-----\n…paste your PEM here…\n-----END CERTIFICATE-----';

export default class X509DecoderComponent extends Component<{ feature?: string }, State> {
    state: State = {
        pemInput: '',
        info: null,
        error: null,
        lib: { kind: 'idle' },
    };

    private async ensureForge(): Promise<any | null> {
        if (this.state.lib.kind === 'ready') return this.state.lib.forge;
        this.setState({ lib: { kind: 'loading' } });
        try {
            const forge = await import('node-forge');
            this.setState({ lib: { kind: 'ready', forge: forge.default || forge } });
            return forge.default || forge;
        } catch (e: any) {
            this.setState({ lib: { kind: 'error', message: e.message || 'Failed to load node-forge' } });
            return null;
        }
    }

    decode = async () => {
        const forge = await this.ensureForge();
        if (!forge) return;
        try {
            const cert = forge.pki.certificateFromPem(this.state.pemInput);
            const fingerprint = forge.md.sha256.create();
            fingerprint.update(forge.asn1.toDer(forge.pki.certificateToAsn1(cert)).getBytes());
            const fp = fingerprint.digest().toHex().match(/.{1,2}/g)?.join(':').toUpperCase() || '';
            const sanExt = cert.extensions.find((e: any) => e.name === 'subjectAltName');
            const sans = sanExt ? sanExt.altNames.map((a: any) => `${a.type === 2 ? 'DNS' : a.type === 7 ? 'IP' : 'X'}: ${a.value || a.ip}`) : [];
            this.setState({
                info: {
                    subject: cert.subject.attributes.map((a: any) => ({ name: a.shortName || a.name, value: a.value })),
                    issuer: cert.issuer.attributes.map((a: any) => ({ name: a.shortName || a.name, value: a.value })),
                    serialNumber: cert.serialNumber,
                    notBefore: new Date(cert.validity.notBefore).toISOString(),
                    notAfter: new Date(cert.validity.notAfter).toISOString(),
                    signatureAlgorithm: forge.pki.oids[cert.signatureOid] || cert.signatureOid,
                    publicKeyType: cert.publicKey.n ? `RSA ${cert.publicKey.n.bitLength()} bits` : 'EC',
                    sans,
                    fingerprintSha256: fp,
                },
                error: null,
            });
        } catch (e: any) {
            this.setState({ error: e.message || 'Failed to decode', info: null });
        }
    };

    render() {
        const { pemInput, info, error, lib } = this.state;
        return (
            <FullscreenHost>
            <div className="row">
                <div className="col-12 col-md-6">
                    <label className={sharedStyles.label}>
                        PEM-encoded certificate
                        <textarea className={`${sharedStyles.textarea} ${styles.mono}`} rows={14} value={pemInput}
                            spellCheck={false}
                            placeholder={SAMPLE_PEM}
                            onChange={e => this.setState({ pemInput: e.target.value, info: null, error: null })} />
                    </label>
                    <button className={sharedStyles.buttonPrimary} onClick={this.decode}
                        disabled={!pemInput.trim() || lib.kind === 'loading'}>
                        {lib.kind === 'loading' ? 'Loading node-forge…' : 'Decode'}
                    </button>
                    {(error || lib.kind === 'error') && (
                        <p className={styles.error} role="alert">{error || (lib as any).message}</p>
                    )}
                </div>
                <div className="col-12 col-md-6">
                    <label className={sharedStyles.label}>Decoded certificate</label>
                    {info ? (
                        <div className={styles.info}>
                            <h4>Subject</h4>
                            <table className={styles.kv}>
                                <tbody>{info.subject.map((a, i) => <tr key={i}><td>{a.name}</td><td>{a.value}</td></tr>)}</tbody>
                            </table>
                            <h4>Issuer</h4>
                            <table className={styles.kv}>
                                <tbody>{info.issuer.map((a, i) => <tr key={i}><td>{a.name}</td><td>{a.value}</td></tr>)}</tbody>
                            </table>
                            <h4>Validity</h4>
                            <table className={styles.kv}>
                                <tbody>
                                    <tr><td>Not Before</td><td>{info.notBefore}</td></tr>
                                    <tr><td>Not After</td><td>{info.notAfter}</td></tr>
                                    <tr><td>Serial</td><td className={styles.mono}>{info.serialNumber}</td></tr>
                                    <tr><td>Signature</td><td>{info.signatureAlgorithm}</td></tr>
                                    <tr><td>Public key</td><td>{info.publicKeyType}</td></tr>
                                </tbody>
                            </table>
                            {info.sans.length > 0 && (
                                <>
                                    <h4>Subject Alternative Names</h4>
                                    <ul className={styles.sans}>{info.sans.map((s, i) => <li key={i}>{s}</li>)}</ul>
                                </>
                            )}
                            <h4>Fingerprint (SHA-256)</h4>
                            <div className={`${styles.fp} ${styles.mono}`}>{info.fingerprintSha256}</div>
                        </div>
                    ) : (
                        <div className={styles.placeholder}>Paste a PEM-encoded certificate on the left and click Decode.</div>
                    )}
                </div>
            </div>

            </FullscreenHost>
        );
    }
}
